import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MicRecorder from "mic-recorder-to-mp3";
// Precisa vir junto com o MicRecorder: forca o buffer do ScriptProcessor e
// impede o audio picotado sob carga. Ver o proprio helper.
import "../../helpers/audioRecorderBufferFix";

import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";

import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ExitToApp from "@mui/icons-material/ExitToApp";
import ForumOutlined from "@mui/icons-material/ForumOutlined";
import GroupAdd from "@mui/icons-material/GroupAdd";
import MicIcon from "@mui/icons-material/Mic";
import SendIcon from "@mui/icons-material/Send";

import Audio from "../Audio";
import ConfirmationModal from "../ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import { AddParticipantsDialog } from "./Dialogs";
import {
  chatTitle,
  extractTicketId,
  formatSize,
  formatTime,
  initials,
  isChatOnline,
  myUnread,
  parseMentions,
  renderBody,
  resolveMentions
} from "./shared";

// MP3 (mesma lib do atendimento): toca em qualquer navegador, inclusive iPhone —
// o formato nativo do MediaRecorder (webm) não toca no Safari.
const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const MessagesArea = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  ...theme.scrollbarStyles,
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  backgroundColor: theme.palette.mode === "dark" ? "#1c1c1c" : "#f5f6f7"
}));

const Bubble = styled(Box, {
  shouldForwardProp: prop => prop !== "mine" && prop !== "highlighted"
})(({ theme, mine, highlighted }) => ({
  alignSelf: mine ? "flex-end" : "flex-start",
  maxWidth: "72%",
  padding: theme.spacing(1, 1.5),
  borderRadius: 12,
  backgroundColor: mine
    ? theme.palette.primary.main
    : theme.palette.mode === "dark"
    ? "#2f2f2f"
    : "#ffffff",
  color: mine ? theme.palette.primary.contrastText : theme.palette.text.primary,
  boxShadow: theme.shadows[1],
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
  // Mensagem que cita você fica marcada: numa conversa longa é o que permite
  // achar o que exige a sua ação.
  ...(highlighted && { borderLeft: `4px solid ${theme.palette.warning.main}` })
}));

const Composer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`
}));

// Dentro de Atendimentos a conversa interna fica exatamente onde estaria a do
// cliente. Esta faixa é o que impede o envio trocado.
const InternalBanner = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 2),
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 193, 7, 0.14)"
      : "rgba(255, 193, 7, 0.18)",
  color: theme.palette.text.secondary,
  fontSize: 12,
  fontWeight: 600
}));

const ConversationPanel = ({ chatId, onBack, embedded = false }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerRef = useRef(null);
  const micStreamRef = useRef(null);
  const recordTimerRef = useRef(null);
  const chatIdRef = useRef(chatId);

  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);

  const scrollToBottom = useCallback(() => {
    window.requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, []);

  const markAsRead = useCallback(async id => {
    try {
      await api.post(`/internal-chats/${id}/read`);
      window.dispatchEvent(new CustomEvent("internalchat:unread-refresh"));
    } catch (err) {
      // Marcar como lido é acessório: se falhar, não atrapalha a conversa.
    }
  }, []);

  useEffect(() => {
    if (!chatId) return;
    let cancelado = false;

    const carregar = async () => {
      setLoading(true);
      setMessages([]);
      try {
        const [{ data: chatData }, { data: msgData }] = await Promise.all([
          api.get(`/internal-chats/${chatId}`),
          api.get(`/internal-chats/${chatId}/messages`)
        ]);
        if (cancelado) return;
        setChat(chatData);
        setMessages(msgData.messages);
        scrollToBottom();
        if (myUnread(chatData, user?.id) > 0) markAsRead(chatId);
      } catch (err) {
        if (!cancelado) toastError(err);
      } finally {
        if (!cancelado) setLoading(false);
      }
    };

    carregar();
    return () => {
      cancelado = true;
    };
  }, [chatId, markAsRead, scrollToBottom, user]);

  useEffect(() => {
    const socket = openSocket();
    if (!socket) return undefined;

    const handler = payload => {
      const { action } = payload || {};

      if (action === "mention") {
        if (Number(payload.message?.senderId) !== Number(user?.id)) {
          toast.warning(
            `${payload.message?.sender?.name || "Alguém"} citou você`
          );
        }
        return;
      }

      if (action === "newMessage") {
        if (String(payload.chat?.id) !== String(chatIdRef.current)) return;
        setChat(payload.chat);
        setMessages(prev =>
          prev.some(m => m.id === payload.message.id)
            ? prev
            : [...prev, payload.message]
        );
        scrollToBottom();
        if (Number(payload.message.senderId) !== Number(user?.id)) {
          markAsRead(payload.chat.id);
        }
        return;
      }

      if (action === "updateChat") {
        if (String(payload.chat?.id) === String(chatIdRef.current)) {
          setChat(payload.chat);
        }
      }
    };

    socket.on("internalChat", handler);
    return () => socket.off("internalChat", handler);
  }, [markAsRead, scrollToBottom, user]);

  const participants = useMemo(() => chat?.participants || [], [chat]);

  const mentionOptions = useMemo(() => {
    const term = mentionQuery.toLowerCase();
    return participants
      .filter(p => Number(p.userId) !== Number(user?.id))
      .filter(p => (p.user?.name || "").toLowerCase().includes(term))
      .slice(0, 6);
  }, [participants, mentionQuery, user]);

  const handleInputChange = event => {
    const value = event.target.value;
    setInput(value);

    const caret = event.target.selectionStart || value.length;
    const upToCaret = value.substring(0, caret);
    const match = /@([\p{L}\s]{0,20})$/u.exec(upToCaret);

    if (match && chat?.isGroup) {
      setMentionQuery(match[1].trim());
      setMentionAnchor(composerRef.current);
    } else {
      setMentionAnchor(null);
      setMentionQuery("");
    }
  };

  const applyMention = name => {
    setInput(prev => prev.replace(/@([\p{L}\s]{0,20})$/u, `@${name} `));
    setMentionAnchor(null);
    setMentionQuery("");
  };

  const registrarMensagem = created => {
    if (!created?.id) return;
    setMessages(prev =>
      prev.some(m => m.id === created.id) ? prev : [...prev, created]
    );
    scrollToBottom();
  };

  const handleSend = async () => {
    const body = input.trim();
    if ((!body && !pendingFile) || !chatId || sending) return;

    setSending(true);
    try {
      let created;
      if (pendingFile) {
        const formData = new FormData();
        formData.append("file", pendingFile);
        if (body) formData.append("body", body);
        const ticketId = extractTicketId(body);
        if (ticketId) formData.append("ticketId", ticketId);
        ({ data: created } = await api.post(
          `/internal-chats/${chatId}/media`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        ));
      } else {
        ({ data: created } = await api.post(
          `/internal-chats/${chatId}/messages`,
          {
            body,
            ticketId: extractTicketId(body),
            mentions: resolveMentions(body, participants)
          }
        ));
      }
      setInput("");
      setPendingFile(null);
      // Entra pela resposta do POST: se o tempo real falhar, quem escreveu
      // ainda vê o que escreveu. O evento chega depois e o dedup por id ignora.
      registrarMensagem(created);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const releaseMic = useCallback(() => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (micStreamRef.current) {
      try {
        micStreamRef.current.getTracks().forEach(t => t.stop());
      } catch (err) {
        // navegador já pode ter encerrado a trilha
      }
      micStreamRef.current = null;
    }
    setRecordSeconds(0);
  }, []);

  useEffect(() => releaseMic, [releaseMic]);

  const handleStartRecording = async () => {
    if (recording || !chatId) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      try {
        await Mp3Recorder.start(stream);
      } catch (err) {
        await Mp3Recorder.start();
      }
      setRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(
        () => setRecordSeconds(s => s + 1),
        1000
      );
    } catch (err) {
      releaseMic();
      toast.error(
        "Não foi possível acessar o microfone. Verifique a permissão do navegador."
      );
    }
  };

  const handleCancelRecording = async () => {
    try {
      await Mp3Recorder.stop().getMp3();
    } catch (err) {
      // descartando mesmo
    }
    setRecording(false);
    releaseMic();
  };

  const handleStopAndSend = async () => {
    if (!recording || !chatId) return;
    setSending(true);
    try {
      const [, blob] = await Mp3Recorder.stop().getMp3();
      setRecording(false);
      releaseMic();

      if (!blob || blob.size < 8000) {
        toast.info("Áudio muito curto — nada enviado.");
        return;
      }

      const formData = new FormData();
      formData.append("file", blob, `audio_${Date.now()}.mp3`);
      const { data: created } = await api.post(
        `/internal-chats/${chatId}/media`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      registrarMensagem(created);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const handlePickFile = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.warn("O anexo precisa ter até 25 MB.");
      return;
    }
    setPendingFile(file);
    event.target.value = "";
  };

  const handleLeave = async () => {
    try {
      await api.delete(`/internal-chats/${chatId}/leave`);
      toast.success("Você saiu do grupo.");
      if (onBack) onBack();
    } catch (err) {
      toastError(err);
    }
  };

  if (loading && !chat) {
    return (
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center", pt: 6 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  if (!chat) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {embedded && (
        <InternalBanner>
          <ForumOutlined sx={{ fontSize: 15 }} />
          <span>CONVERSA INTERNA DA EQUIPE — o cliente não recebe nada daqui</span>
        </InternalBanner>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1.5,
          borderBottom: theme => `1px solid ${theme.palette.divider}`
        }}
      >
        {onBack && (
          <Button size="small" onClick={onBack}>
            Voltar
          </Button>
        )}
        <Avatar sx={{ width: 36, height: 36, fontSize: 14 }}>
          {chat.isGroup ? (
            <ForumOutlined fontSize="small" />
          ) : (
            initials(chatTitle(chat, user?.id))
          )}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap>
            {chatTitle(chat, user?.id)}
          </Typography>
          <Typography variant="caption" color="textSecondary" noWrap>
            {chat.isGroup
              ? `${participants.length} participantes`
              : isChatOnline(chat, user?.id)
              ? "Online"
              : "Offline"}
          </Typography>
        </Box>
        {chat.isGroup && (
          <>
            <Tooltip title="Adicionar participantes">
              <IconButton onClick={() => setAddOpen(true)}>
                <GroupAdd />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sair do grupo">
              <IconButton onClick={() => setConfirmLeave(true)}>
                <ExitToApp />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      <MessagesArea>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!loading && !messages.length && (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ textAlign: "center", mt: 2 }}
          >
            Nenhuma mensagem. Diga olá.
          </Typography>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {messages.map(message => {
          const mine = Number(message.senderId) === Number(user?.id);
          const mentioned = parseMentions(message.mentions).includes(
            Number(user?.id)
          );
          return (
            <Bubble key={message.id} mine={mine} highlighted={mentioned}>
              {!mine && chat.isGroup && (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, display: "block" }}
                >
                  {message.sender?.name}
                </Typography>
              )}

              {message.mediaUrl && message.mediaType === "image" && (
                <Box
                  component="img"
                  src={message.mediaUrl}
                  alt={message.mediaName}
                  onClick={() => window.open(message.mediaUrl, "_blank")}
                  sx={{
                    display: "block",
                    maxWidth: 260,
                    maxHeight: 260,
                    width: "100%",
                    objectFit: "cover",
                    borderRadius: 1,
                    cursor: "pointer",
                    mb: message.body ? 0.75 : 0
                  }}
                />
              )}

              {message.mediaUrl && message.mediaType === "audio" && (
                <Box sx={{ minWidth: 240 }}>
                  <Audio url={message.mediaUrl} />
                </Box>
              )}

              {message.mediaUrl &&
                message.mediaType !== "image" &&
                message.mediaType !== "audio" && (
                  <Link
                    href={message.mediaUrl}
                    target="_blank"
                    rel="noopener"
                    underline="none"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      p: 0.75,
                      mb: message.body ? 0.75 : 0,
                      borderRadius: 1,
                      color: "inherit",
                      backgroundColor: mine
                        ? "rgba(255,255,255,0.18)"
                        : "action.hover"
                    }}
                  >
                    <DescriptionOutlined fontSize="small" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {message.mediaName}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {formatSize(message.mediaSize)}
                      </Typography>
                    </Box>
                  </Link>
                )}

              {message.body && (
                <Typography variant="body2">
                  {renderBody(message.body, participants)}
                </Typography>
              )}

              {message.ticketId ? (
                <Chip
                  size="small"
                  icon={<ConfirmationNumberOutlined />}
                  label={`Abrir ticket #${message.ticketId}`}
                  onClick={() => navigate(`/tickets/${message.ticketId}`)}
                  sx={{
                    mt: 0.75,
                    cursor: "pointer",
                    ...(mine && {
                      backgroundColor: "rgba(255,255,255,0.22)",
                      color: "inherit",
                      "& .MuiChip-icon": { color: "inherit" },
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.34)" }
                    })
                  }}
                />
              ) : null}
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "right", opacity: 0.7 }}
              >
                {formatTime(message.createdAt)}
              </Typography>
            </Bubble>
          );
        })}
        <div ref={bottomRef} />
      </MessagesArea>

      {pendingFile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderTop: theme => `1px solid ${theme.palette.divider}`
          }}
        >
          <DescriptionOutlined fontSize="small" />
          <Typography variant="body2" noWrap sx={{ flex: 1 }}>
            {pendingFile.name} ({formatSize(pendingFile.size)})
          </Typography>
          <IconButton size="small" onClick={() => setPendingFile(null)}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Composer ref={composerRef}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handlePickFile}
          style={{ display: "none" }}
        />

        {recording ? (
          <>
            <Tooltip title="Descartar áudio">
              <IconButton onClick={handleCancelRecording}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: "error.main",
                  animation: "sabePulse 1s ease-in-out infinite",
                  "@keyframes sabePulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.25 }
                  }
                }}
              />
              <Typography variant="body2" color="textSecondary">
                Gravando… {Math.floor(recordSeconds / 60)}:
                {String(recordSeconds % 60).padStart(2, "0")}
              </Typography>
            </Box>
            <Tooltip title="Enviar áudio">
              <span>
                <IconButton
                  color="primary"
                  onClick={handleStopAndSend}
                  disabled={sending}
                >
                  {/* Encerra a gravação E envia — por isso ícone de envio, não
                      o quadrado de "parar", que prometeria só interromper. */}
                  {sending ? <CircularProgress size={22} /> : <SendIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title="Anexar arquivo (até 25 MB)">
              <IconButton onClick={() => fileInputRef.current?.click()}>
                <AttachFileOutlined />
              </IconButton>
            </Tooltip>
            <TextField
              placeholder="Mensagem para a equipe (#1234 cita um ticket, @ chama alguém)"
              fullWidth
              size="small"
              multiline
              maxRows={4}
              value={input}
              onChange={handleInputChange}
              onKeyDown={e => {
                if (e.key === "Escape") {
                  setMentionAnchor(null);
                  return;
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            {!input.trim() && !pendingFile ? (
              <Tooltip title="Gravar áudio">
                <IconButton color="primary" onClick={handleStartRecording}>
                  <MicIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={sending}
              >
                {sending ? <CircularProgress size={22} /> : <SendIcon />}
              </IconButton>
            )}
          </>
        )}
      </Composer>

      <Popper
        open={!!mentionAnchor && mentionOptions.length > 0}
        anchorEl={mentionAnchor}
        placement="top-start"
        style={{ zIndex: 1300 }}
      >
        <Paper elevation={4} sx={{ minWidth: 220, py: 0.5 }}>
          <List dense disablePadding>
            {mentionOptions.map(option => (
              <ListItemButton
                key={option.userId}
                onClick={() => applyMention(option.user?.name)}
              >
                <ListItemAvatar sx={{ minWidth: 40 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                    {initials(option.user?.name)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={option.user?.name} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </Popper>

      <AddParticipantsDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        chat={chat}
        onUpdated={setChat}
      />

      <ConfirmationModal
        title="Sair do grupo"
        open={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        onConfirm={handleLeave}
      >
        Você deixará de receber as mensagens deste grupo. O histórico continua
        para os demais participantes.
      </ConfirmationModal>
    </Box>
  );
};

export default ConversationPanel;
